import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CustomerInfo, LOG_LEVEL, Purchases, PurchasesOffering } from '@revenuecat/purchases-capacitor';
import { sharedConfig } from 'src/environments/shared-config';
import { UserData } from '../auth-service/user-data.model';
import { ModalController } from '@ionic/angular';
import { UserInfoService } from '../auth-service/user-info-service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { PaywallPopupComponent } from 'src/app/components/paywall-popup/paywall-popup.component';
import { EventService } from '../events/event.service';
import { OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { PromptService } from '../promtp-service/prompt.service';
import { TranslatorService } from '../translator-service/translator.service';
import { OrganizationService } from '../organization-service/organization.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private personId?: string;
  private isNative = Capacitor.isNativePlatform();

  constructor(private modalCtrl: ModalController,
    private _environmentService: EnvironmentConfigService,
    private _promptService: PromptService,
    private _translationService: TranslatorService,
    private _organizationService: OrganizationService
  ) {
  
  }


  private async initPayCat(userInfo: UserData): Promise<void> {
    if (!userInfo || !userInfo.tokenClaims || !userInfo.tokenClaims.personId) {
      await this._promptService.showError(this._translationService.translateText('subscription-service.errors.person-record-empty'));
      this.personId = undefined;
      throw new Error('PersonId not found - user not logged in');
    }

    const env = await this._environmentService.EnsureNetworkConfigurationForToken();

    if (!env || !env.countryCode) {
      await this._promptService.showError(this._translationService.translateText('subscription-service.errors.person-record-empty'));
      this.personId = undefined;
      throw new Error('Unable to determine region');
    }


    const personId = env.countryCode.toLocaleLowerCase().trim() + '_' + userInfo.tokenClaims.personId;

    if (this.personId === personId) return;

    this.personId = personId;

    if (this.isNative) {

      const allowUAT = await this._environmentService.AllowUAT();
      let logLevel = LOG_LEVEL.WARN;
      if (allowUAT)
        logLevel = LOG_LEVEL.DEBUG;
      try {
        await Purchases.setLogLevel({ level: logLevel });
        const apiKey =
          Capacitor.getPlatform() === 'ios'
            ? sharedConfig.revenueCatIOSKey
            : sharedConfig.revenueCatAndroidKey;

        await Purchases.configure({
          apiKey,
          appUserID: this.personId,
        });
      } catch (error) {
        this.personId = undefined;
        throw new Error('Unable to init Purchases Platform');
      }
    }
  }

  private async getOfferings(): Promise<PurchasesOffering | null> {
    if (this.isNative) {
      const offerings = await Purchases.getOfferings();
      return offerings?.current;
    } else {
      // const allowUAT = await this._environmentService.AllowUAT();
      // if (!allowUAT) return null;
      // return this.getMockProducts();
      return null;
    }
  }

  private async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isNative) return null;

    let result = await Purchases.getCustomerInfo();
    return result.customerInfo;
  }

  public async checkAndSubscribe(callback: (success: boolean) => void, showPaywall: boolean = false): Promise<void> {
    let subscriptionRequired = await this._organizationService.SubscriptionRequired();

    if (subscriptionRequired == null) {
      await this._promptService.showError(this._translationService.translateText('subscription-service.errors.subscription-requires-app'));
      callback(false);
      return;
    }

    if (subscriptionRequired == false && !showPaywall) {
      callback(true); // nothing to do here
      return;
    }

    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      const allowUAT = await this._environmentService.AllowUAT();
      if (allowUAT) {
        callback(true); // no subscription required in UAT
        return;
      }
    }

    if (subscriptionRequired && !this.isNative) {
      await this._promptService.showError(this._translationService.translateText('subscription-service.errors.subscription-requires-app'));
      callback(false);
      return;
    }

    const usr = await UserInfoService.getUserInfo();
    if (!usr) {
      await this._promptService.showError(this._translationService.translateText('subscription-service.errors.subscription-status-unavailable'));
      callback(false);
      return;
    }
    await this.initPayCat(usr);
    let customerInfo = await this.getCustomerInfo();
    let activeEntitlements = customerInfo?.entitlements?.active;
    let alreadySubscribed = activeEntitlements && Object.keys(activeEntitlements).length > 0;
    if (alreadySubscribed && !showPaywall) {
      callback(true);
      return;
    }

    // Build subscription timing info
    let subscriptionStatus: SubscriptionStatus | null = null;
    if (alreadySubscribed && activeEntitlements) {
      const entitlement = Object.values(activeEntitlements)[0]; // assumes 1 active entitlement
      subscriptionStatus = {
        purchaseDate: entitlement.latestPurchaseDate ? new Date(entitlement.latestPurchaseDate) : undefined,
        expirationDate: entitlement.expirationDate ? new Date(entitlement.expirationDate) : undefined,
        originalPurchaseDate: entitlement.originalPurchaseDate ? new Date(entitlement.originalPurchaseDate) : undefined,
      };
    }
    const offerings = await this.getOfferings();
    const modal = await this.modalCtrl.create({
      id: 'paywall-modal',
      component: PaywallPopupComponent,
      componentProps: {
        offerings,
        isSubscribed: alreadySubscribed,
        subscriptionStatus: subscriptionStatus,
        cssClass: 'paywall-modal'
      },
      animated: true,
    });

    await modal.present();
    await modal.onDidDismiss();
    customerInfo = await this.getCustomerInfo();
    activeEntitlements = customerInfo?.entitlements?.active;
    alreadySubscribed = activeEntitlements && Object.keys(activeEntitlements).length > 0;
    callback(alreadySubscribed === true);
  }


}

export interface SubscriptionStatus {
  purchaseDate?: Date,
  expirationDate?: Date,
  originalPurchaseDate?: Date
}